import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { RootState } from '../store';
import {
  fetchOrganization,
  updateOrganization,
  uploadOrganizationLogo,
  deleteOrganizationLogo,
} from '../store/slices/organizationSlice';

const OrganizationSettings = () => {
  const dispatch = useDispatch();
  const { organization, loading, error } = useSelector((state: RootState) => state.organization);
  
  const [editMode, setEditMode] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    // Mock organization ID - replace with actual user's organization ID
    const orgId = 1;
    dispatch(fetchOrganization(orgId) as any);
  }, [dispatch]);

  useEffect(() => {
    if (organization) {
      setOrganizationName(organization.name);
    }
  }, [organization]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'File size must be less than 2MB', severity: 'error' });
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile || !organization) return;

    try {
      await dispatch(uploadOrganizationLogo({ orgId: organization.id, file: selectedFile }) as any);
      setSelectedFile(null);
      setPreviewUrl('');
      setSnackbar({ open: true, message: 'Logo uploaded successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to upload logo', severity: 'error' });
    }
  };

  const handleDeleteLogo = async () => {
    if (!organization) return;

    try {
      await dispatch(deleteOrganizationLogo(organization.id) as any);
      setDeleteDialogOpen(false);
      setSnackbar({ open: true, message: 'Logo deleted successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete logo', severity: 'error' });
    }
  };

  const handleUpdateOrganization = async () => {
    if (!organization) return;

    try {
      await dispatch(updateOrganization({ 
        orgId: organization.id, 
        data: { name: organizationName } 
      }) as any);
      setEditMode(false);
      setSnackbar({ open: true, message: 'Organization updated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update organization', severity: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (organization) {
      setOrganizationName(organization.name);
    }
  };

  if (!organization) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading organization settings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Organization Settings</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Organization Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Organization Information</Typography>
            {!editMode ? (
              <Button 
                variant="outlined" 
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
              >
                Edit
              </Button>
            ) : (
              <Box>
                <Button onClick={handleCancelEdit} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateOrganization}
                  disabled={loading}
                >
                  Save
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mr: 2, minWidth: 120 }}>
              Organization Name:
            </Typography>
            {editMode ? (
              <TextField
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                fullWidth
                size="small"
              />
            ) : (
              <Typography variant="body1">{organization.name}</Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Logo Management */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Logo Management</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
            {/* Current Logo */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Logo</Typography>
              {organization.logo_url ? (
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={organization.logo_url}
                    alt={`${organization.name} Logo`}
                    sx={{ width: 120, height: 120, border: '2px solid #e0e0e0' }}
                    variant="square"
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ position: 'absolute', top: -8, right: -8 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ) : (
                <Avatar
                  sx={{ width: 120, height: 120, border: '2px dashed #ccc', bgcolor: 'grey.100' }}
                  variant="square"
                >
                  No Logo
                </Avatar>
              )}
            </Box>

            {/* Upload New Logo */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Upload New Logo</Typography>
              
              <Paper
                sx={{
                  p: 3,
                  border: '2px dashed #ccc',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => document.getElementById('logo-upload')?.click()}
              >
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Click to upload or drag and drop
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  PNG, JPG up to 2MB (Recommended: 200x60px)
                </Typography>
              </Paper>

              {previewUrl && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
                  <Avatar
                    src={previewUrl}
                    alt="Preview"
                    sx={{ width: 120, height: 120, border: '2px solid #e0e0e0' }}
                    variant="square"
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleUploadLogo}
                      disabled={loading}
                      sx={{ mr: 1 }}
                    >
                      Upload Logo
                    </Button>
                    <Button onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl('');
                    }}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          <Alert severity="info">
            <Typography variant="body2">
              The logo will appear in the dashboard header for all users in your organization.
              Changes take effect immediately on the next page load.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Delete Logo Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Logo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the organization logo? This will revert to the default logo
            for all users in your organization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteLogo} color="error" variant="contained">
            Delete Logo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrganizationSettings;
